/**
 * Derived geometry, memoized per element object.
 *
 * Elements are immutable, so a WeakMap keyed on the object needs no explicit
 * invalidation. Keeping these caches OUTSIDE the elements means they can never
 * be persisted to IndexedDB or leak into a JSON export.
 */

import type { SlateElement } from '../scene/types';
import { drawableFor, type Drawable, type MeasureText } from './drawable';
import { flattenDrawable, type Polyline } from './flatten';

interface Entry {
  measure: MeasureText;
  drawable: Drawable;
  polylines: Polyline[] | null;
}

const cache = new WeakMap<SlateElement, Entry>();

function entryFor(el: SlateElement, measure: MeasureText): Entry {
  const hit = cache.get(el);
  // The measurer is part of the cache key: tests inject a different one.
  if (hit && hit.measure === measure) return hit;
  const entry: Entry = { measure, drawable: drawableFor(el, measure), polylines: null };
  cache.set(el, entry);
  return entry;
}

export function drawableOf(el: SlateElement, measure: MeasureText): Drawable {
  return entryFor(el, measure).drawable;
}

export function polylinesOf(el: SlateElement, measure: MeasureText): readonly Polyline[] {
  const entry = entryFor(el, measure);
  if (entry.polylines === null) {
    if (el.type === 'text') {
      // Text is hit and bound as its solid box; glyph-accurate outlines would be
      // surprising to click and pointless to bind to.
      entry.polylines = [
        {
          points: [
            { x: 0, y: 0 },
            { x: el.width, y: 0 },
            { x: el.width, y: el.height },
            { x: 0, y: el.height },
            { x: 0, y: 0 },
          ],
          closed: true,
          filled: true,
          strokeWidth: 0,
        },
      ];
    } else {
      entry.polylines = flattenDrawable(entry.drawable);
    }
  }
  return entry.polylines;
}
